const {
  fetchJSON,
  trapFocus,
  disableTrapFocus,
  setCookie,
  getCookie,
} = window.BAO.Utils

class CountryConfirmation extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    if (window.location.href.indexOf('#geo') > -1) {
      return this.forceRedirect()
    }

    if (this.isReturnsPage && !this.hasNarvarParams) {
      this.redirectToReveni()
      return
    }

    this.checkRedirect()

    this.userInCorrectLocation = null
    this.detectedCountryCode = null

    // If the cookie banner is due to appear, we add a delay to prevent country confirmation from displaying before
    const trackingConsent = window.Shopify?.customerPrivacy?.getTrackingConsent()
    const delayMs = (trackingConsent === 'no_interaction') === true ? 3000 : 0

    setTimeout(() => {
      this.initialise()
    }, delayMs)
  }

  async initialise () {
    const viewedCountryCode = getCookie(this.viewedStorageName)

    // Once we've re-checked geolocation for this browser session, don't keep re-fetching on
    // every page - the viewed cookie is trusted for the rest of the session either way.
    if (viewedCountryCode !== '' && this.hasCheckedLocationThisSession) {
      return
    }

    if (this.searchParams.has('country')) {
      this.setViewedStorage(this.searchParams.get('country'))
      return
    }

    this.fetchLocation(viewedCountryCode)
  }

  checkRedirect () {
    let redirectEnabled = true

    if (
      this.searchParams.has('noredirect') ||
      getCookie(this.noRedirectStorageName) === 'true'
    ) {
      redirectEnabled = false

      if (getCookie(this.noRedirectStorageName) !== 'true') {
        this.setNoRedirectStorage()
      }
    }

    if (!this.searchParams.has('country') && this.redirectUrl) {
      const target = new URL(this.redirectUrl)

      // Detect current path, and searchParams
      const currentPath = this.pathnameWithoutLanguageSubdirectory
      const redirectUrlParts = this.redirectUrl.split('?')
      const redirectDestination = new URL(
        redirectUrlParts[0] + (currentPath !== '/' ? currentPath : ''),
      )

      const redirectSearchParams = new URLSearchParams(
        redirectUrlParts[1] || '',
      )

      // Merge current URL search params into the redirect URL params (preserving existing params)
      this.searchParams.forEach((value, key) => {
        if (!redirectSearchParams.has(key)) {
          redirectSearchParams.set(key, value)
        }
      })
      redirectDestination.search = redirectSearchParams.toString()

      // Compare just the host (domain + subdomain)
      if (window.location.host !== target.host) {
        if (redirectEnabled) {
          window.location.href = `${redirectDestination}`
        } else {
          this.forceOpen(true)
        }
      }
    }
  }

  fetchLocation (viewedCountryCode = '') {
    fetchJSON(`/browsing_context_suggestions.json`, {
      method: 'GET',
    }).then(res => {
      const countryCode = res.detected_values.country.handle
      const countryName = res.detected_values.country.name

      this.detectedCountryCode = countryCode
      this.markLocationCheckedThisSession()

      // Viewed cookie still matches where we've detected the visitor is - nothing's changed
      // since they last dismissed/confirmed the modal, so don't show it again.
      if (viewedCountryCode !== '' && viewedCountryCode === countryCode) {
        return
      }

      this.setupCountrySwitcherModal(countryCode, countryName)
    })
  }

  disableCloseButton () {
    if (this.els?.close?.exists) {
      this.els.close.elements.forEach(closeEL => {
        closeEL.setAttribute('disabled', 'true')
        closeEL.setAttribute('aria-hidden', 'true')
      })
    }
  }

  forceRedirect () {
    fetchJSON(`/browsing_context_suggestions.json`, {
      method: 'GET',
    }).then(res => {
      const userCountry = res.detected_values.country.handle
      const site = this.countries[userCountry]?.site

      if (this.isReturnsPage && !this.hasNarvarParams && site) {
        this.redirectToReveni(site)
        return
      }

      const searchParams = new URLSearchParams(window.location.search)
      const paramsObject = {}
      searchParams.forEach((value, key) => {
        paramsObject[key] = value
      })

      // Filter out parameters starting with "country"
      const filteredParamsObject = Object.fromEntries(
        Object.entries(paramsObject).filter(
          ([key]) => !key.startsWith('country'),
        ),
      )

      const filteredSearchParams = new URLSearchParams(filteredParamsObject)

      const searchParamsString = filteredSearchParams.toString()
      window.location.href = `${
        this.sites[this.countries[userCountry].site].url
      }${
        this.pathnameWithoutLanguageSubdirectory
      }?country=${userCountry}&${searchParamsString}&${
        this.sites[this.countries[userCountry].site].currency
      }`
    })
  }

  setupListeners () {
    super.setupListeners()

    this.onConfirmClick = this.onConfirmClick.bind(this)
    this.onShippingDrawerClick = this.onShippingDrawerClick.bind(this)
    this.onCloseClick = this.onCloseClick.bind(this)

    if (this.els?.confirm.exists) {
      this.els.confirm.elements.forEach(selectEl => {
        this.listeners.add(selectEl, 'click', this.onConfirmClick)
      })
    }

    if (this.els?.close?.exists) {
      this.els.close.elements.forEach(closeEL => {
        this.listeners.add(closeEL, 'click', this.onCloseClick)
      })
    }

    if (this.els?.shippingDrawer?.exists) {
      this.els.shippingDrawer.elements.forEach(button => {
        this.listeners.add(button, 'click', this.onShippingDrawerClick)
      })
    }

    document.addEventListener('shopify:section:select', e => {
      if (e.detail.sectionId === 'country-confirmation') {
        this.forceOpen(true)
      }
    })
  }

  forceOpen () {
    if (this.noChoiceMade) {
      this.setAttribute('aria-hidden', 'false')

      if (document.body.classList.contains('util-DrawersOpen')) return
      document.body.classList.add('util-SiteOverflowed')
      document.body.classList.add('country-confirmation-open')
    }
  }

  setViewedStorage (
    countryCode = this.detectedCountryCode || this.dataset.countryCode,
  ) {
    setCookie(this.viewedStorageName, countryCode, this.storageDurationDays)
  }

  markLocationCheckedThisSession () {
    sessionStorage.setItem(this.sessionCheckedStorageName, 'true')
  }

  setRedirectSiteStorage (url) {
    if (url) {
      setCookie(
        this.redirectSiteStorageName,
        url,
        this.storageDurationDays,
        true,
      )
    }
  }

  setNoRedirectStorage () {
    setCookie(
      this.noRedirectStorageName,
      'true',
      this.storageDurationDays,
      true,
    )
  }

  onConfirmClick (event) {
    event.preventDefault()

    this.setViewedStorage()

    if (this.userInCorrectLocation !== null) {
      this.pushEvent(
        this.userInCorrectLocation
          ? 'Welcome_pop_up_confirm'
          : 'Store_switcher_pop_up_confirm',
      )
    }

    const searchParams = new URLSearchParams(window.location.search)
    const paramsObject = {}
    searchParams.forEach((value, key) => {
      paramsObject[key] = value
    })

    // Filter out parameters starting with "country"
    const filteredParamsObject = Object.fromEntries(
      Object.entries(paramsObject).filter(([key]) => !key.startsWith('country')),
    )

    const filteredSearchParams = new URLSearchParams(filteredParamsObject)
    const searchParamsString = filteredSearchParams
      .toString()
      .replace('language_prompt=true', '')

    let subdirectory = ''
    if (event.currentTarget.dataset.subdirectory) {
      subdirectory = `/${event.currentTarget.dataset.subdirectory}`
    }

    if (event.currentTarget.hasAttribute('data-url')) {
      const destinationUrl = event.currentTarget.dataset.url

      const isSeperateDomain =
        new URL(destinationUrl).origin !== window.location.origin

      if (isSeperateDomain) {
        this.setRedirectSiteStorage(
          `${destinationUrl}${subdirectory}?country=${event.currentTarget.dataset.code}`,
        )
      }

      const href = `${destinationUrl}${subdirectory}${this.pathnameWithoutLanguageSubdirectory}?country=${event.currentTarget.dataset.code}&${searchParamsString}`
      window.location.href = href
    } else {
      this.close()
    }

    document.body.classList.remove('country-confirmation-open')
  }

  onShippingDrawerClick (event) {
    if (this.userInCorrectLocation !== null) {
      this.pushEvent(
        this.userInCorrectLocation
          ? 'Welcome_pop_up_change_country'
          : 'Store_switcher_pop_up_change_country',
      )
    }

    this.close(event)
  }

  onCloseClick (event) {
    event?.preventDefault()

    this.pushEvent('Welcome_pop_up_dismiss')

    this.close()
  }

  close () {
    this.setViewedStorage()

    this.setAttribute('aria-hidden', 'true')

    disableTrapFocus(this)

    if (document.body.classList.contains('util-DrawersOpen')) return
    document.body.classList.remove('util-SiteOverflowed')
    document.body.classList.remove('country-confirmation-open')
  }

  setupCountrySwitcherModal (countryCode, countryName) {
    this.userInCorrectLocation = countryCode === this.dataset.countryCode

    if (
      this.userInCorrectLocation &&
      this.dataset.showModalToCorrectLocation === 'false'
    ) {
      return
    }

    if (!this.userInCorrectLocation) {
      this.updateUsps(countryCode)
      this.updateCountryName(countryCode, countryName)
      this.disableCloseButton()
    }

    this.setDefaultConfirmButtonURL(countryCode)

    trapFocus(this)

    this.setAttribute('aria-hidden', 'false')
    document.body.classList.add('util-SiteOverflowed')
    document.body.classList.add('country-confirmation-open')
  }

  updateUsps (countryCode) {
    this.els.usps?.elements.forEach(el => {
      const countryCodes = el.dataset.countryCodes.split(',')
      el.ariaHidden = !countryCodes.includes(countryCode)
    })
  }

  updateCountryName (countryCode, countryName) {
    this.els.country?.elements.forEach(el => {
      el.textContent = this.countryNameAppended(countryCode, countryName)
    })
  }

  setDefaultConfirmButtonURL (countryCode) {
    const site = window.theme.countries[countryCode].site

    this.els.confirm.elements.forEach(confirmEl => {
      if (countryCode !== this.dataset.countryCode) {
        confirmEl.dataset.url = this.sites[site].url
      }

      confirmEl.dataset.code = countryCode
    })
  }

  pushEvent (eventName) {
    window.dataLayer = window.dataLayer || []

    window.dataLayer.push({
      event: eventName,
    })
  }

  countryNameAppended (countryCode, countryName) {
    const countryCodeUppercase = countryCode?.toUpperCase()
    const prependCodes = ['US', 'GB', 'NL', 'PH', 'AE', 'MV', 'BS', 'SC', 'GM']

    if (prependCodes.includes(countryCodeUppercase) === true) {
      return `the ${countryName}`
    }

    return countryName
  }

  redirectToReveni (site = null) {
    const reveniUrlsBySite = {
      UK: 'https://returns.self-portrait.com/',
      EU: 'https://returns-eu.self-portrait.com',
      US: 'https://returns-us.self-portrait.com',
    }

    if (site === null) {
      site = this.dataset.site
    }

    const destination =
      site && reveniUrlsBySite[site] ? reveniUrlsBySite[site] : false

    if (destination) {
      window.location.href = destination
    }
  }

  get isReturnsPage () {
    return (
      this.pathnameWithoutLanguageSubdirectory === '/apps/returns' ||
      window.location.pathname === '/apps/returns'
    )
  }

  get hasNarvarParams () {
    const p = this.searchParams
    return (
      p.has('order') ||
      p.has('email') ||
      p.has('tracking_number') ||
      p.has('csid')
    )
  }

  get pathnameWithoutLanguageSubdirectory () {
    // Get pathname, check the first part (i.e. /pages), and make sure that it matches the expected
    // Shopify pathnames. If it doesn't, we assume it's a market subdirectory and remove it.
    const url = new URL(window.location.href)

    let pathname = url.pathname
    const pathnameSplit = pathname.split('/')
    if (!this.shopifyPathnames.includes(pathnameSplit[1])) {
      pathname = pathname.replace(`/${pathnameSplit[1]}`, '')
    }

    return pathname
  }

  get noChoiceMade () {
    return getCookie(this.viewedStorageName) === ''
  }

  get hasCheckedLocationThisSession () {
    return sessionStorage.getItem(this.sessionCheckedStorageName) === 'true'
  }

  get redirectUrl () {
    if (
      getCookie(this.redirectSiteStorageName) &&
      getCookie(this.redirectSiteStorageName) !== ''
    ) {
      return getCookie(this.redirectSiteStorageName)
    } else {
      return false
    }
  }

  get searchParams () {
    return new URLSearchParams(window.location.search)
  }

  get viewedStorageName () {
    return `BAO-country-confirmation-viewed`
  }

  get redirectSiteStorageName () {
    return `BAO-country-confirmation-redirect-site`
  }

  get noRedirectStorageName () {
    return `BAO-country-confirmation-no-redirect`
  }

  get sessionCheckedStorageName () {
    return `BAO-country-confirmation-checked-session`
  }

  get storageDurationDays () {
    return parseInt(this.dataset.storageDurationDays, 10) || 14 // days
  }

  get sites () {
    return window.theme.sites
  }

  get countries () {
    return window.theme.countries
  }

  get shopifyPathnames () {
    return [
      'pages',
      'products',
      'collections',
      'blogs',
      'policies',
      'search',
      'account',
      'cart',
      '404',
    ]
  }
}

customElements.define('country-confirmation', CountryConfirmation)
